import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { Label } from './Label';

export enum TextareaSubTextPosition {
  ABOVE = 'above',
  BELOW = 'below',
}

export type TextareaProps = {
  label?: React.ReactNode;
  error?: string;
  variant?: 'outlined' | 'contained' | 'text';
  color?: 'primary' | 'secondary' | 'default';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  required?: boolean;
  disabled?: boolean;
  subText?: string;
  subTextPosition?: TextareaSubTextPosition;
  footer?: React.ReactNode;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

const sizeClasses = {
  small: 'px-2 py-1 text-sm',
  medium: 'px-3 py-2.5 text-base',
  large: 'px-4 py-3 text-lg',
};

const variantClasses = {
  outlined: {
    base: 'border',
    primary: 'border-neutral-200 focus:border-primary',
    secondary: 'border-neutral-200 focus:border-neutral-400',
    default: 'border-neutral-200',
  },
  contained: {
    base: 'border-0',
    primary: 'bg-neutral-50 focus:bg-neutral-50',
    secondary: 'bg-neutral-50 focus:bg-neutral-50',
    default: 'bg-neutral-50 focus:bg-neutral-50',
  },
  text: {
    base: 'border-0',
    primary: 'focus:bg-neutral-50',
    secondary: 'focus:bg-neutral-50',
    default: 'focus:bg-neutral-50',
  },
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      variant = 'outlined',
      color = 'primary',
      size = 'medium',
      className,
      required = false,
      disabled = false,
      subText,
      rows = 4,
      subTextPosition = TextareaSubTextPosition.BELOW,
      footer,
      ...props
    },
    ref,
  ) => {
    const variantClass = variantClasses[variant];
    const sizeClass = sizeClasses[size];

    return (
      <div className="w-full">
        {label && (
          <Label
            htmlFor={props.id}
            className={clsx(error ? 'text-error' : 'text-neutral-600')}
            required={required}
          >
            {label}
          </Label>
        )}

        {subText && subTextPosition === TextareaSubTextPosition.ABOVE && (
          <p className="mb-1 text-sm text-neutral-400">{subText}</p>
        )}

        <textarea
          ref={ref}
          rows={rows}
          {...props}
          disabled={disabled}
          className={clsx(
            'w-full rounded-md transition-colors duration-200',
            'focus:outline-none',
            'focus:ring-primary/20',
            variantClass.base,
            error ? 'error' : variantClass[color],
            error ? 'focus:ring-error/20' : 'focus:ring-primary/20',
            sizeClass,
            disabled && 'cursor-not-allowed bg-neutral-50 text-neutral-400',
            className,
          )}
        />

        {footer && (
          <div className="absolute bottom-4 left-2 text-neutral-400 text-sm">
            {footer}
          </div>
        )}

        {(error ||
          (subText && subTextPosition === TextareaSubTextPosition.BELOW)) && (
          <p
            className={clsx(
              'mt-1 text-sm',
              error ? 'text-error' : 'text-neutral-400',
            )}
          >
            {error || subText}
          </p>
        )}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
